import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { HelpCircle, MessageCircle, Phone, Mail, Clock, Search, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Support = () => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Your inquiry has been submitted. We\'ll get back to you soon!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const faqs = [
    {
      question: 'What is the Food Stock Market concept?',
      answer: 'Fudco introduces India\'s first Food Stock Market where food prices fluctuate based on real-time demand. When a dish is in high demand, its price may increase, and when demand is low, you can grab great deals. It\'s a fun, dynamic way to order food!',
    },
    {
      question: 'How do I place an order?',
      answer: 'Simply sign up on Fudco, browse restaurants and their menus, add items to your cart, and checkout. You can track your order in real-time from preparation to delivery.',
    },
    {
      question: 'What payment methods are accepted?',
      answer: 'We accept UPI, credit/debit cards, net banking, and cash on delivery. All online payments are secured with industry-standard encryption.',
    },
    {
      question: 'How can I become a restaurant partner?',
      answer: 'Visit our Restaurant Partner page and fill out the application form. Our team will review your application and get in touch with you within 2-3 business days.',
    },
    {
      question: 'How can I join as a delivery partner?',
      answer: 'You can apply through our Delivery Partner page. We welcome partners with bicycles, two-wheelers, and other vehicles. Flexible hours and competitive earnings await!',
    },
    {
      question: 'What if I have an issue with my order?',
      answer: 'You can reach out to our 24/7 customer support through the app or by calling our helpline. We aim to resolve all issues within 24 hours.',
    },
    {
      question: 'Is my personal data safe?',
      answer: 'Absolutely! We use bank-grade encryption and follow strict data protection policies. Your personal information is never shared with third parties without your consent.',
    },
    {
      question: 'Can I cancel my order?',
      answer: 'You can cancel your order before the restaurant starts preparing it. Refunds for cancelled orders are processed within 5-7 business days.',
    },
  ];

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen pt-20" data-testid="support-page">
      {/* Hero Section */}
      <section className="fudco-hero-gradient fudco-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-[#2D8B2E] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <HelpCircle className="w-4 h-4" />
            Help & Support
          </div>
          <h1 className="fudco-heading-1 text-gray-900 mb-6">
            How Can We <span className="text-[#2D8B2E]">Help</span> You?
          </h1>
          <p className="fudco-body-lg mb-8">
            Find answers to common questions or reach out to our support team.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-14 pl-12 rounded-full text-lg"
              data-testid="search-input"
            />
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="fudco-section bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="fudco-card text-center hover-lift" data-testid="contact-phone-card">
              <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-2xl inline-block mb-4">
                <Phone className="w-8 h-8" />
              </div>
              <h3 className="fudco-heading-3 text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-600 mb-4">Talk to our support team</p>
              <p className="text-[#2D8B2E] font-semibold text-lg">+91 1800-XXX-XXXX</p>
            </Card>

            <Card className="fudco-card text-center hover-lift" data-testid="contact-email-card">
              <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-2xl inline-block mb-4">
                <Mail className="w-8 h-8" />
              </div>
              <h3 className="fudco-heading-3 text-gray-900 mb-2">Email Us</h3>
              <p className="text-gray-600 mb-4">We'll respond within 24 hours</p>
              <p className="text-[#2D8B2E] font-semibold text-lg">service@fudco.in</p>
            </Card>

            <Card className="fudco-card text-center hover-lift" data-testid="contact-hours-card">
              <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-2xl inline-block mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="fudco-heading-3 text-gray-900 mb-2">Support Hours</h3>
              <p className="text-gray-600 mb-4">Available for you</p>
              <p className="text-[#2D8B2E] font-semibold text-lg">24/7 Support</p>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="fudco-section bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="fudco-heading-2 text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white rounded-2xl border border-green-100 px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#2D8B2E]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {filteredFaqs.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">No results found for "{searchQuery}"</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Form */}
      <section className="fudco-section bg-white">
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 md:p-12" data-testid="contact-form-card">
            <div className="text-center mb-8">
              <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-2xl inline-block mb-4">
                <MessageCircle className="w-8 h-8" />
              </div>
              <h2 className="fudco-heading-2 text-gray-900 mb-2">Still Need Help?</h2>
              <p className="text-gray-600">Send us a message and we'll get back to you.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                    className="h-12 rounded-xl"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="h-12 rounded-xl"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="How can we help?"
                  required
                  className="h-12 rounded-xl"
                  data-testid="input-subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your issue or question..."
                  rows={5}
                  required
                  className="rounded-xl"
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full text-lg font-semibold"
                data-testid="submit-btn"
              >
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Send Message'}
              </Button>
            </form>
          </Card>
        </div>
      </section>
    </div>
  );
};
